import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Search, Bell, Clock, MapPin, ArrowDown } from 'lucide-react';

const HowToUse = ({ onBack }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const steps = [
    {
      number: 1,
      title: 'Just ask',
      description: 'Type what you want in plain English. No filters, no forms. Just talk to GrotN like you would to a friend.',
      example: '"Find me a 2-bedroom in San Francisco under $3500"',
      icon: Search
    },
    {
      number: 2,
      title: 'We search',
      description: 'Our AI scans thousands of listings and finds what matches. We check descriptions, locations, everything.',
      example: 'We look for quiet neighborhoods, check distances, match your budget',
      icon: Search
    },
    {
      number: 3,
      title: 'Save it',
      description: 'Found something close? Click the bell to save your search. We\'ll email you when new places pop up.',
      example: 'You can save up to 3 searches at once',
      icon: Bell
    },
    {
      number: 4,
      title: 'Get alerts',
      description: 'We check for new properties every hour. When we find matches, you get an email. Simple.',
      example: 'No spam, just the good stuff',
      icon: Clock
    }
  ];

  const examples = [
    {
      query: 'Studio in San Francisco under $2500',
      result: 'Finds studios matching your budget'
    },
    {
      query: '2-bedroom near VU Amsterdam, 10 min walk',
      result: 'Checks walking distance automatically'
    },
    {
      query: 'Quiet neighborhood, max $3500, at least 500 sqft',
      result: 'Matches by vibe, not just keywords'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <div className="min-h-screen bg-white pt-16 sm:pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="mb-12 sm:mb-16 text-center"
        >
          <motion.button
            type="button"
            variants={itemVariants}
            whileHover={{ x: -3 }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => {
              e.preventDefault();
              onBack();
            }}
            className="mb-8 p-2 hover:bg-gray-100 rounded-full transition-colors inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowRight className="w-5 h-5 rotate-180" />
            <span className="hidden sm:inline">Back</span>
          </motion.button>
          <motion.h1
            variants={itemVariants}
            className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-4"
          >
            How GrotN works
          </motion.h1>
          <motion.p
            variants={itemVariants}
            className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto"
          >
            No complicated forms. Just tell us what you want. We figure out the rest.
          </motion.p>
        </motion.div>

        {/* Steps */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="mb-12 sm:mb-20 space-y-6 sm:space-y-8"
        >
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.number}
                variants={itemVariants}
                whileHover={{ x: 5 }}
                className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-start"
              >
                <div className="flex-shrink-0 flex items-center gap-4">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: index * 0.15, type: 'spring', stiffness: 200 }}
                    className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gray-900 text-white flex items-center justify-center font-bold text-lg sm:text-xl relative"
                  >
                    {step.number}
                    <motion.div
                      className="absolute inset-0 rounded-full bg-gray-900"
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.5, 0, 0.5]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: index * 0.3
                      }}
                    />
                  </motion.div>
                  {index < steps.length - 1 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      transition={{ delay: index * 0.15 + 0.3 }}
                      className="hidden sm:block"
                    >
                      <ArrowDown className="w-6 h-6 text-gray-300" />
                    </motion.div>
                  )}
                </div>
                <div className="flex-1 pt-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Icon className="w-5 h-5 text-gray-400" />
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900">{step.title}</h3>
                  </div>
                  <p className="text-gray-600 mb-3 leading-relaxed text-sm sm:text-base">{step.description}</p>
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.15 + 0.2 }}
                    className="bg-gray-50 rounded-lg p-3 border border-gray-200"
                  >
                    <p className="text-xs text-gray-500 mb-1">Example:</p>
                    <p className="text-sm font-medium text-gray-900">{step.example}</p>
                  </motion.div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Examples */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-12 sm:mb-16"
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">Try these</h2>
          <div className="space-y-4">
            {examples.map((example, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 + index * 0.1 }}
                whileHover={{ x: 5, scale: 1.02 }}
                className="bg-gray-50 rounded-xl p-4 sm:p-5 border border-gray-200 hover:border-gray-300 transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">"{example.query}"</p>
                    <p className="text-xs sm:text-sm text-gray-600">{example.result}</p>
                  </div>
                  <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Tips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="bg-gray-900 text-white rounded-2xl p-6 sm:p-8"
        >
          <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Quick tips</h2>
          <ul className="space-y-3 sm:space-y-4 text-gray-300">
            {[
              'Be specific about what matters to you. "Quiet neighborhood" works better than just "nice area"',
              'Mention distances if location matters. We calculate walking/cycling routes automatically',
              'Save your best searches. We\'ll email you when new places match',
              'Check your profile to see search history and manage your scouts'
            ].map((tip, index) => (
              <motion.li
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.1 + index * 0.1 }}
                className="flex items-start gap-3 text-sm sm:text-base"
              >
                <span className="text-white font-bold mt-1 flex-shrink-0">•</span>
                <span>{tip}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>
      </div>
    </div>
  );
};

export default HowToUse;
